import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { concatenateName } from "@/utils/functions/helperFunctions";
import { FormTableSkeletonDesktop, FormTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_SEARCH_RESULTS, TABLE_HEADERS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { MobileLabelValue } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import OwnershipSelectCheckbox from "@/components/buttons/OwnershipSelectCheckbox";
import { transferFormOwnership } from "@/services/form.service";
import { invalidateOwnershipTransferListCaches } from "@/utils/listPageCache";
import {
    buildTransferOwnershipPayload,
    type OwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { successToasterMessage, errorToasterMessage } from "@/utils/functions/toaster";
import { TRANSFER_OWNERSHIP_SUCCESS } from "@/utils/constants/messages";

interface FormTableProps {
    forms: any;
    loadingData: boolean;
    setForms: any;
    dataLength: number;
    sorting: string;
    isLoading: boolean;
    allFetched?: boolean;
    setSorting: ({}) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
    selection: OwnershipSelection;
    showTransferActions: boolean;
    /** Detail route prefix, e.g. /form-details or /web-form-details */
    detailsPath?: string;
    transferTitle?: string;
    transferDescription?: string;
}

function FormTable(props: FormTableProps) {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedForm, setSelectedForm] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const detailsPath = props.detailsPath || '/form-details';
    const transferTitle = props.transferTitle || LABELS.TRANSFER_FORM_OWNERSHIP;
    const transferDescription = props.transferDescription || LABELS.TRANSFER_FORM_OWNERSHIP_DESCRIPTION;
    const formIcon = detailsPath.includes('web-form') ? 'ni-globe' : 'ni-notes-alt';
    const { selection, showTransferActions } = props;

    const formIds = (props.forms ?? []).map((form: any) => form.id).filter(Boolean);
    const columnCount = showTransferActions ? 6 : 4;
    const isLoadingMore = props.isLoading && !props.dataLoad;

    const handleOpenTransferModal = (form: any) => {
        setTransferLoading(false);
        setSelectedForm(form);
        setShowTransferModal(true);
    };

    const handleCloseTransferModal = () => {
        setShowTransferModal(false);
        setSelectedForm(null);
    };

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedForm) return;

        setTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            await transferFormOwnership(selectedForm.id, payload);
            successToasterMessage(TRANSFER_OWNERSHIP_SUCCESS);
            handleCloseTransferModal();
            selection.deselect(selectedForm.id);
            invalidateOwnershipTransferListCaches("form");
            props.setPageNo(1);
            props.resetDataQueries();
        } catch (error) {
            console.log("Error transferring form ownership:", error);
            errorToasterMessage(error);
        } finally {
            setTransferLoading(false);
        }
    };

    const renderFormStatusBadge = (form: any, badgeId?: string) => (
        <span
            id={badgeId}
            className={`badge badge-sm badge-dim text-capitalize gs-form-table-status-badge ${
                form?.is_accepting_responses ? "badge-outline-success" : "badge-outline-secondary"
            }`}
        >
            {form?.is_accepting_responses ? "Active" : "Inactive"}
        </span>
    );

    const renderSelectCheckbox = (formId: string, key: number, suffix = "") => (
        <OwnershipSelectCheckbox
            id={`${props.id}-select-${key}${suffix}`}
            checked={selection.isSelected(formId)}
            onCheckedChange={() => selection.toggle(formId)}
            onClick={(event) => event.stopPropagation()}
            aria-label={LABELS.TRANSFER_OWNERSHIP}
        />
    );

    return (
        <>
            {isCompactLayout ? (
                props.forms && !props.dataLoad ? (
                    props.forms?.length > 0 ? (
                        <>
                            <div className="envelope_table">
                                {props.forms?.map((form: any, key: number) => (
                                    <div
                                        className={`envelope_tr gs-mobile-list-card${selection.isSelected(form.id) ? " gs-table-row-selected" : ""}`}
                                        key={form.id}
                                    >
                                        {showTransferActions ? (
                                            <div className="gs-mobile-select-checkbox">
                                                {renderSelectCheckbox(form.id, key, "-mb")}
                                            </div>
                                        ) : (
                                            <div className="gs-mobile-card-icon">
                                                <em className={`icon ni ${formIcon} icons`} id={`${props.id}-icon`}></em>
                                            </div>
                                        )}
                                        <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                            {form.name}
                                            <div className="text-dark">
                                                <span className="fw-bold">Template: </span>
                                                <span className="tb-lead-sub" id={`${props.id}-created-by-${key}-mb`}>
                                                    {form.template?.id ? (
                                                        <Link className="link link-primary link-sm" to={"/template-details/" + form.template.id}>
                                                            {form.template?.name}
                                                        </Link>
                                                    ) : (
                                                        form.template?.name || "—"
                                                    )}
                                                </span>
                                            </div>
                                            <MobileLabelValue
                                                label="Responses: "
                                                value={`${form?.total_responses ? form?.total_responses : "0"}${form?.configurations?.max_allowed_responses === 10000 ? "" : `/${form?.configurations?.max_allowed_responses}`}`}
                                            />
                                            <MobileLabelValue
                                                label={`${TABLE_HEADERS.STATUS}: `}
                                                value={renderFormStatusBadge(form)}
                                                id={`${props.id}-status-${key}-mb`}
                                            />
                                            <MobileLabelValue
                                                label={`${TABLE_HEADERS.SENDER}: `}
                                                value={concatenateName(form.created_by?.first_name, form.created_by?.last_name)}
                                            />
                                            {showTransferActions ? (
                                                <div className="gs-mobile-transfer-ownership">
                                                    <TransferOwnershipButton
                                                        id={`form-transfer-ownership-btn-${key}-mb`}
                                                        iconId={`form-transfer-ownership-icon-${key}-mb`}
                                                        onClick={() => handleOpenTransferModal(form)}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                        <MobileListChevron
                                            id={`${props.id}-redirect-${key}-mb`}
                                            onClick={() => navigate(`${detailsPath}/${form.id}`)}
                                        />
                                    </div>
                                ))}
                            </div>
                            {isLoadingMore ? (
                                <FormTableSkeletonMobile
                                    rowCount={2}
                                    showActionColumn={showTransferActions}
                                />
                            ) : null}
                        </>
                    ) : props.searchItem ? (
                        <NoDataAvailable>
                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                        </NoDataAvailable>
                    ) : (
                        <NoDataAvailable>
                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                        </NoDataAvailable>
                    )
                ) : (
                    <FormTableSkeletonMobile showActionColumn={showTransferActions} />
                )
            ) : (
                <table
                    className={`nk-tb-list nk-tb-ulist table-weight gs-form-table${showTransferActions ? " gs-form-table--with-actions" : ""}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                >
                    <colgroup>
                        {showTransferActions ? <col className="gs-form-col-select" /> : null}
                        <col className="gs-form-col-name" />
                        <col className="gs-form-col-responses" />
                        <col className="gs-form-col-status" />
                        <col className="gs-form-col-owner" />
                        {showTransferActions ? <col className="gs-form-col-actions" /> : null}
                    </colgroup>
                    <thead>
                        <tr className="nk-tb-item nk-tb-head">
                            {showTransferActions ? (
                                <th className="nk-tb-col gs-form-col-select align-middle">
                                    <OwnershipSelectCheckbox
                                        id={`${props.id}-select-all`}
                                        checked={
                                            selection.areAllSelected(formIds)
                                                ? true
                                                : selection.areSomeSelected(formIds)
                                                  ? "indeterminate"
                                                  : false
                                        }
                                        onCheckedChange={() => selection.toggleAll(formIds)}
                                        aria-label="Select all forms"
                                    />
                                </th>
                            ) : null}
                            <th className="nk-tb-col gs-form-col-name">
                                <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                            </th>
                            <th className="nk-tb-col gs-form-col-responses">
                                <span id={`${props.id}-responses-th`}>{TABLE_HEADERS.RESPONSES}</span>
                            </th>
                            <th className="nk-tb-col gs-form-col-status">
                                <span id={`${props.id}-status-th`}>{TABLE_HEADERS.STATUS}</span>
                            </th>
                            <th className="nk-tb-col gs-form-col-owner">
                                <span id={`${props.id}-owner-th`}>{TABLE_HEADERS.OWNER}</span>
                            </th>
                            {showTransferActions ? (
                                <th className="nk-tb-col gs-form-col-actions">
                                    <span id={`${props.id}-actions-th`}>{TABLE_HEADERS.ACTIONS}</span>
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    {props.forms && !props.dataLoad ? (
                        props.forms?.length > 0 ? (
                            <tbody>
                                {props.forms.map((form: any, key: number) => (
                                    <tr
                                        className={`nk-tb-item${selection.isSelected(form.id) ? " gs-table-row-selected" : ""}`}
                                        key={key}
                                    >
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-form-col-select align-middle">
                                                {renderSelectCheckbox(form.id, key)}
                                            </td>
                                        ) : null}
                                        <td className="nk-tb-col gs-form-col-name subject-col">
                                            <Link to={`${detailsPath}/` + form?.id} className="project-title">
                                                <div className="icon mr-2 fs-5 mt-1">
                                                    <em className={`icon ni ${formIcon} text-gray`}></em>
                                                </div>
                                                <div className="data" style={{ marginTop: 4, maxWidth: "80%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                                    <div className="info">
                                                        <div className="text-dark name tb-col-md">{form.name}</div>
                                                        <div className="meta">
                                                            <span className="version">
                                                                {form.template?.id ? (
                                                                    <Link className="link link-primary link-sm" to={"/template-details/" + form.template.id}>
                                                                        {form.template?.name}
                                                                    </Link>
                                                                ) : (
                                                                    form.template?.name || "—"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col gs-form-col-responses tb-col-mb text-left">
                                            <div className="meta">
                                                <span className="text-dark">{`${form?.total_responses ? form?.total_responses : "0"}${form?.configurations?.max_allowed_responses === 10000 ? "" : `/${form?.configurations?.max_allowed_responses}`}`}</span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-form-col-status tb-col-mb">
                                            {renderFormStatusBadge(form, `${props.id}-status-${key}`)}
                                        </td>
                                        <td className="nk-tb-col gs-form-col-owner tb-col-mb text-left">
                                            <div className="meta">
                                                <span className="text-dark">
                                                    {concatenateName(form.owner?.first_name, form.owner?.last_name)}
                                                </span>
                                            </div>
                                        </td>
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-form-col-actions">
                                                <TransferOwnershipButton
                                                    id={`form-transfer-ownership-btn-${key}`}
                                                    iconId={`form-transfer-ownership-icon-${key}`}
                                                    onClick={() => handleOpenTransferModal(form)}
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                                {isLoadingMore ? (
                                    <FormTableSkeletonDesktop rowCount={2} showActionColumn={showTransferActions} />
                                ) : null}
                            </tbody>
                        ) : props.searchItem ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columnCount}>
                                        <NoDataAvailable>
                                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan={columnCount}>
                                        <NoDataAvailable>
                                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        )
                    ) : (
                        <tbody>
                            <FormTableSkeletonDesktop showActionColumn={showTransferActions} />
                        </tbody>
                    )}
                </table>
            )}

            {selectedForm && (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={handleCloseTransferModal}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={selectedForm?.owner?.id}
                    title={transferTitle}
                    description={transferDescription}
                />
            )}
        </>
    );
}

export default FormTable;
